import { Component, ElementRef, EventEmitter, inject, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { TilesService } from '../../../../core/services/tiles.service';
import { TilePollingService, PollConsumerId } from '../../../../core/services/tile-polling.service';
import { FormulaStalenessService } from '../../../../core/services/formula-staleness.service';
import { FormulaInputStaleness } from '../../../../shared/models/formula-staleness';
import {
  Tile,
  TILE_TYPE_SIMPLE_MULTISTATE,
  TileId
} from '../../../../shared/models/tile';
import { TileElemSetup } from '../../../../shared/models/tile-template/interfaces';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TileValueParser, TileValueType } from '../../../../shared/models/tile-value-parser';
import moment from 'moment';
import { TileValue } from '../../../../shared/models/tile-value';
import { ConditionMode, SimpleStructure, TileStructure } from '../../../../shared/models/structure';
import { ShadeToolsService } from '../../../../core/services/shade-tools.service';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ColorOption } from '../../../../shared/models/appearance';
import { AlarmsService } from '../../../../core/services/alarms.service';
import { AlarmEvent } from '../../../../shared/models/alarm';
import { makeAlarmMenuOptions } from '../../../../shared/utils/alarm.util';
import { TileIcon } from '../../../../shared/models/tile-icon';
import { TileHistoryService } from '../../../../core/services/tile-history.service';
import { OptionGroup } from '../../../../shared/models/option';
import { PopoverMenuHoverColors } from '../../popover-menu/popover-menu.component';
import { SessionService } from '../../../../core/services/session.service';
import { PermissionsService } from 'src/core/services/permissions.service';
import { ObjectType } from 'src/shared/models/object.model';
import { Permission } from 'src/shared/models/fga/permission';
import {TileMenuOptionId } from 'src/shared/constants/menu-option-ids';
import { DEFAULT_STALE_THRESHOLD_MINUTES, DATA_AGE_REFRESH_INTERVAL_MS } from '@shared/constants/tile-config.constants';
import { MotiChatService } from 'src/moti/services/moti-chat.service';
import { ViewportIntersectionService } from '../../../../core/services/viewport-intersection.service';
import { calculateDataAge } from '../../../../shared/utils/data-age.util';

@Component({
    template: ``,
    styleUrls: ['./tile-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class TileBaseComponent implements OnInit, OnDestroy, OnChanges {
  abstract elemDef: TileElemSetup;

  @Output() openTileEditor: EventEmitter<TileId> = new EventEmitter<TileId>();

  tile: Tile;
  structure: TileStructure;
  displayName: string;
  displayIcon: TileIcon;
  displayColor: ColorOption;
  displayAlarmText: string;
  displayValue: string = '';
  displayTimestamp: string = '';

  alarmEvent: AlarmEvent;
  tileValueParser: TileValueParser;
  sendingValue: TileValueType = null;
  rawValue: TileValueType = null;
  rawTimestamp: string = '';
  loading: boolean = true;
  timeAgoInterval: number = 0;
  previousDisplayValue: string = '';
  previousTimestamp: string = '';
  isOutdated: boolean = false;
  staleValue: string = '';
  formulaStaleness: FormulaInputStaleness | null = null;

  _showSchedules: boolean = false;

  popoverPositions: ConnectedPosition[] = [{
    originX: 'center',
    originY: 'center',
    overlayX: 'center',
    overlayY: 'center',
  }];

  tileMenuOpen: boolean = false;
  tileMenuLoading: boolean = false;
  private chatPermissionChecked: boolean = false;
  tileMenuTitle: string = 'Tile Features';
  tileMenuHoverColor: PopoverMenuHoverColors = '';
  tileMenuOptions: OptionGroup = new OptionGroup([{
    id: TileMenuOptionId.CONFIGURE,
    name: 'Configure',
    callback: () => {
      this.editTile();
    },
  }]);

  alarmMenuOptions: OptionGroup;

  private tileValueSub: Subscription;
  private alarmSub: Subscription;
  private viewportSub: Subscription;
  private isInViewport: boolean = false;
  private isFetchingValues: boolean = false;
  /** ID of the tile currently registered with the polling service, if any. */
  private polledTileId: TileId | null = null;
  private readonly pollConsumerId: PollConsumerId = crypto.randomUUID();
  private readonly formulaStalenessService = inject(FormulaStalenessService);
  private formulaStalenessSub?: Subscription;
  private formulaStalenessKey?: string;


  protected constructor(
    protected tilesService: TilesService,
    protected tilePollingService: TilePollingService,
    protected shadeTools: ShadeToolsService,
    protected alarmsService: AlarmsService,
    protected tileHistoryService: TileHistoryService,
    protected permissionsService: PermissionsService,
    protected session: SessionService,
    protected motiChatService: MotiChatService,
    protected elementRef: ElementRef,
    protected viewportService: ViewportIntersectionService,
  ) {
  }


  ngOnInit(): void {
    this.processElemDefInput();
    this.subscribeToTileValues();
    this.subscribeToAlarmEvents();
    this.setupViewportObserver();

    this.onInit();
  }


  ngOnDestroy() {
    this.teardownViewportObserver();
    this.tilePollingService.stopAllPolling(this.pollConsumerId);
    this.onDestroy();

    this.tileValueSub?.unsubscribe();
    this.alarmSub?.unsubscribe();
    this.viewportSub?.unsubscribe();
    this.stopWatchingFormulaStaleness();
  }


  get staleTooltip(): string {
    return `Last updated ${this.displayTimestamp}`;
  }

  get displayStaleValue(): string {
    return this.staleValue || this.formulaStaleness?.longestStaleAgeText || '';
  }


  ngOnChanges(changes: SimpleChanges) {
    /**
     * @see elemDef {TileElemSetup}
     */
    const current = changes.elemDef?.currentValue;
    const previous = changes.elemDef?.previousValue;
    const isNewElemDef= JSON.stringify(current) !== JSON.stringify(previous);

    if (current && isNewElemDef) {
      this.elemDef = current;
      this.processElemDefInput();
    }
  }


  private processElemDefInput() {
    const newTile = this.elemDef.tile;

    // If polling is active and the tile has changed, swap registrations so the
    // old tile stops being polled and the new one takes its place immediately.
    if (this.isFetchingValues && this.polledTileId !== null && this.polledTileId !== newTile.ID) {
      this.tilePollingService.stopPolling(this.pollConsumerId, this.polledTileId);
      this.tilePollingService.startPolling(this.pollConsumerId, newTile);
      this.polledTileId = newTile.ID;
    }

    this.tile = newTile;
    this.structure = this.tile.Structure;
    this.subscribeToFormulaStaleness();
    // Reset so the next poll reflects any config changes (e.g. transformation) even
    // if the device reading timestamp hasn't changed.
    this.rawTimestamp = '';
    this.displayName = this.elemDef.displayName || this.elemDef.tile.displayName;

    this.prepareTileMenuOptions();
    this.setColorAndIcon();
    this.setTileMenuHoverColor();
  }


  private subscribeToFormulaStaleness(): void {
    const structure = this.tile.Structure as SimpleStructure;
    const key = `${this.tile.ID}:${structure?.DeviceID}:${structure?.PointID}`;

    if (this.formulaStalenessKey === key) {
      return;
    }

    this.stopWatchingFormulaStaleness();
    this.formulaStalenessKey = key;
    this.formulaStaleness = null;
    this.syncFormulaStaleness();
  }


  private syncFormulaStaleness(): void {
    if (!this.tile?.isFormulaTile()
      || !this.shouldFetchValues()) {
      this.stopWatchingFormulaStaleness();
      return;
    }

    if (!this.formulaStalenessSub) {
      this.formulaStalenessSub = this.formulaStalenessService.watchInputStaleness$(this.tile)
        .subscribe(staleness => this.formulaStaleness = staleness);
    }
  }


  private stopWatchingFormulaStaleness(): void {
    this.formulaStalenessSub?.unsubscribe();
    this.formulaStalenessSub = undefined;
  }


  private prepareTileMenuOptions() {
    if (this.tile.typeSupportsTileHistory()) {
      if (!this.tileMenuOptions.options.find(o => o.id === TileMenuOptionId.HISTORY)) {
        this.tileMenuOptions.options.push({
          id: TileMenuOptionId.HISTORY,
          name: 'Show History',
          callback: () => {
            this.loadHistory();
          },
        });
      }
    }

    if (this.tile.typeSupportsReadWrite() && (this.tile.Structure as SimpleStructure).Writable) {
      if (!this.tileMenuOptions.options.find(o => o.id === TileMenuOptionId.SCHEDULES)) {
        this.tileMenuOptions.options.push({
          id: TileMenuOptionId.SCHEDULES,
          name: 'Schedules',
          callback: () => {
            this.showSchedules();
          },
        });
      }
    }
  }

  private checkChatPermission() {
    if (this.chatPermissionChecked) return;

    this.chatPermissionChecked = true;
    this.tileMenuLoading = true;

    this.permissionsService.queryPermissions({
      UserID: this.session.userId,
      ObjectID: this.tile.ID,
      ObjectType: ObjectType.TILE,
      Permissions: [Permission.CAN_CHAT, Permission.CAN_CONFIGURE]
    }).subscribe({
      next: (response) => {
        const canChat = response.Results.find(r => r.Permission === Permission.CAN_CHAT)?.Allowed;
        const canConfigure = response.Results.find(r => r.Permission === Permission.CAN_CONFIGURE)?.Allowed;

        // Add chat option if permitted
        if (canChat && !this.tileMenuOptions.options.find(o => o.id === TileMenuOptionId.CHAT)) {
          this.tileMenuOptions.options.push({
            id: TileMenuOptionId.CHAT,
            name: 'Chat',
            icon: 'fa-kit fa-chat',
            callback: () => this.motiChatService.openChat(this.tile.ID, this.displayName || this.tile.Name, ObjectType.TILE),
          });
        }

        // Remove configure option if not permitted
        if (!canConfigure) {
          this.tileMenuOptions.options = this.tileMenuOptions.options.filter(o => o.id !== TileMenuOptionId.CONFIGURE);
        }

        this.tileMenuLoading = false;
      },
      error: () => this.tileMenuLoading = false
    });
  }


  openTileMenu() {
    this.tileMenuOpen = true;
    this.checkChatPermission();
  }

  get facetValues(): string[] {
    return this.elemDef?.tile?.facetValues ?? [];
  }

  get displayedMenuOptions(): OptionGroup {
    if (this.tileMenuLoading) {
      return new OptionGroup([{
        id: 'loading',
        name: '',
        icon: 'fa-regular fa-spin fa-loader',
      }]);
    }
    return this.tileMenuOptions;
  }


  showAlarmOptionsMenu() {
    this.alarmMenuOptions = makeAlarmMenuOptions(this.alarmEvent, this.alarmsService, () => {
      this.alarmEvent = undefined;
    });
  }


  /**
   * Sets the name of the CSS class that should be used to colorize the tile.
   * Checks if alarm condition is met and updates display accordingly.
   */
  private setColorAndIcon() {
    // Don't evaluate alarm conditions before the first real value is received.
    // rawValue starts as null, which coerces to 0 for analog tiles and can
    // falsely satisfy threshold conditions (e.g. criticalLow <= 0), causing
    // tiles to flash alarm colors on initial load.
    if (this.loading) {
      this.displayColor = this.elemDef.displayColor;
      this.displayIcon = this.elemDef.displayIcon;
      this.displayAlarmText = '';
      return;
    }

    // Check if alarm condition is met (only call getFulfilledCondition once)
    const cond = this.tile.getFulfilledCondition(this.rawValue);
    const alarmColor = this.tile.getAlarmColor(cond);

    if (alarmColor && cond) {
      // Alarm active - use alarm color and condition's icon
      this.displayColor = alarmColor;
      this.displayIcon = cond.Icon || this.tile.Appearance.Icon;
      this.displayAlarmText = cond.DisplayText || '';
    } else {
      // No alarm - use tile's default appearance
      this.displayColor = this.elemDef.displayColor;
      this.displayIcon = this.elemDef.displayIcon;
      this.displayAlarmText = '';
    }
  }


  private setTileMenuHoverColor() {
    // const color = this.tile.Appearance.Color;
    //
    // if (color === '' || color.indexOf('indigo') > -1) {
    //   this.tileMenuHoverColor = 'indigo';
    // } else if (color.indexOf('alt') > -1) {
    //   this.tileMenuHoverColor = 'indigo-dark';
    // } else if (color.indexOf('green') > -1) {
    //   this.tileMenuHoverColor = 'green';
    // }
  }

  private editTile() {
    if (this.tile.ID) {
      this.tilesService.canConfigureTile$(this.tile.ID).subscribe({
        next: (canConfigure) => {
          if (canConfigure) {
          this.openTileEditor.emit(this.tile.ID);
        } else {
            this.shadeTools.errorDialog(`You don't have permissions to configure this tile`);
          }
        },
        error: () => {
          this.shadeTools.errorDialog(`You don't have permissions to configure this tile`);
        }
      });
    }
  }


  /**
   * Triggers the history chart view
   */
  private loadHistory() {
    this.tileHistoryService.addToDrawerTiles([this.tile]);
  }



  /**
   * Updates the stale data age display (e.g., "15m", "2h", "45d") for data
   * 10 minutes or older, and the outdated flag for data 30+ days old
   */
  private getDataAgeSeconds(time: string): number {
    return moment().add(this.tilesService.serverTimeOffset || 0, 'milliseconds').diff(moment.utc(time), 'seconds');
  }

  protected updateStaleDataAge(ageInSeconds: number): void {
    const result = calculateDataAge(ageInSeconds, { minAgeMinutes: DEFAULT_STALE_THRESHOLD_MINUTES });
    this.isOutdated = result.isStale;
    this.staleValue = result.dataAge;
  }


  private resetTimeAgoInterval(): void {
    clearInterval(this.timeAgoInterval);
    this.timeAgoInterval = window.setInterval(() => {
      // Update stale data age (which also updates stale flag)
      if (this.rawTimestamp) {
        this.updateStaleDataAge(this.getDataAgeSeconds(this.rawTimestamp));
      }
    }, DATA_AGE_REFRESH_INTERVAL_MS);
  }


  protected startFetchingTileValues() {
    if (!this.tile.typeSupportsPolling()) {
      return;
    }

    if (!this.isFetchingValues) {
      this.tilePollingService.startPolling(this.pollConsumerId, this.tile);
      this.polledTileId = this.tile.ID;
      this.isFetchingValues = true;
    }
  }

  protected stopFetchingTileValues() {
    if (this.isFetchingValues && this.polledTileId !== null) {
      this.tilePollingService.stopPolling(this.pollConsumerId, this.polledTileId);
      this.polledTileId = null;
      this.isFetchingValues = false;
    }
  }


  /**
   *
   * @param val
   * @protected
   */
  protected sendTileValue(val: TileValue): void {
    if (this.sendingValue !== null) {
      return;
    }

    if (this.tile.typeSupportsReadWrite() && !(this.tile.Structure as SimpleStructure).Writable) {
      return;
    }

    this.sendingValue = val.Value;

    this.tilesService.sendTileValue$(val).subscribe({
      error: () => {
        this.sendingValue = null;
      },
      complete: () => {
        this.rawValue = val.Value;
        this.tileValueParser.rawValue = val.Value;
        this.displayValue = this.tileValueParser.getDisplayValue();
        this.sendingValue = null;
        this.setColorAndIcon();
      },
    });
  }


  /**
   * For clearing BACnet priority array values
   * @protected
   */
  protected sendNullTileValue(): void {
    this.sendTileValue({
      TileID: this.tile.ID,
      Value: null,
    });
  }


  /**
   *
   * @param val
   * @protected
   */
  protected sendBooleanTileValue(val: boolean): void {
    this.sendTileValue({
      TileID: this.tile.ID,
      Value: val,
    });
  }


  /**
   *
   * @param val
   * @protected
   */
  protected sendAnalogTileValue(val: number): void {
    this.sendTileValue({
      TileID: this.tile.ID,
      Value: val,
      Unconverted: true,
    });
  }


  /**
   *
   * @param val
   * @protected
   */
  protected sendMultistateTileValue(val: number): void {
    this.sendTileValue({
      TileID: this.tile.ID,
      Value: val,
    });
  }


  protected abstract onInit(): void;


  protected abstract onDestroy(): void;


  private subscribeToAlarmEvents(): void {
    this.alarmSub = this.alarmsService.activeEvents$.pipe(
      map(
        (events) => {
          return events.find(e => e.ObjectID === this.tile.ID && e.ObjectType === ObjectType.TILE);
        },
      ),
    ).subscribe(
      (e) => {
        this.alarmEvent = e;
        this.setColorAndIcon();
        // Re-evaluate viewport conditions when alarm status changes
        this.handleViewportChange();
      },
      () => {

      },
    );
  }


  private subscribeToTileValues(): void {
    if (this.tileValueSub) {
      return;
    }

    this.tileValueSub = this.tilesService.tileValues$.pipe(
      map(tv => tv[this.tile.ID]),
      filter(tv => !!tv),
    ).subscribe(
      (value) => {
        this.loading = false;

        const _moment = moment.utc(value.Time);

        this.updateStaleDataAge(this.getDataAgeSeconds(value.Time));

        if (value.Time > this.rawTimestamp) {
          const localTime = _moment.local();

          this.rawValue = value.Value;
          this.rawTimestamp = value.Time;

          if (!this.tileValueParser) {
            this.tileValueParser = new TileValueParser(this.tile, this.rawValue);
          } else {
            this.tileValueParser.tile = this.tile;
            this.tileValueParser.rawValue = this.rawValue;
          }

          let newValue = this.tileValueParser.getDisplayValue();

          if (this.displayValue !== newValue) {
            this.previousDisplayValue = this.displayValue;
            this.previousTimestamp = this.displayTimestamp;
          }

          this.displayValue = newValue;
          this.displayTimestamp = localTime.format(`YYYY-MM-DD HH:mm:ss (h:mma)`);

          this.resetTimeAgoInterval();
          this.setColorAndIcon();
        }
      },
    );
  }


  showSchedules() {
    this._showSchedules = true;
  }


  hideSchedules() {
    this._showSchedules = false;
  }

  private setupViewportObserver(): void {
    if (this.tile && this.elementRef.nativeElement) {
      this.viewportSub = this.viewportService.observe$(
        this.elementRef.nativeElement
      ).subscribe((isVisible) => {
        this.isInViewport = isVisible;
        this.handleViewportChange();
      });
    }
  }

  private teardownViewportObserver(): void {
    if (this.elementRef.nativeElement) {
      this.viewportService.unobserve(this.elementRef.nativeElement);
    }
  }

  private handleViewportChange(): void {
    this.syncFormulaStaleness();

    if (this.shouldFetchValues()) {
      this.startFetchingTileValues();
    } else {
      this.stopFetchingTileValues();
    }
  }

  private shouldFetchValues(): boolean {
    // Always fetch if tile has active alarm (requirement from description)
    if (this.alarmEvent) {
      return true;
    }

    // Only fetch if tile is in viewport
    return this.isInViewport;
  }
}
